<template>
  <div>
    <vue-loading :active="isLoading" :is-full-page="true" />

    <div v-if="config.SHOW_ACCOUNT_BARCODE" class="order-details-qrcode-row">
      <div class="order-details-qrcode-full">
        <QrcodeVue :value="orderDetails.barcode" class="qrcode-img" />
      </div>
    </div>

    <div v-if="orderDetails">
      <dl>
        <div v-for="row in orderDetailRows" :key="row.key">
          <dt>{{ $t(`orderDetails.label.${row.key}`) }}:</dt>
          <dd>{{ row.value }}</dd>
        </div>
      </dl>
    </div>

    <h2 id="order-details-title" class="sr-only">
      {{ $t('orderDetails.label.table') }}
    </h2>
    <Table
      small
      :items="orderDetails.menuItems"
      :fields="fields"
      :responsive="true"
      :striped="true"
      :hover="true"
      @sort-changed="onSortChanged"
      :tbody-tr-attr="rowIdentifier"
      :aria-label="$t('orderDetails.label.table')"
    >
      <template #custom-foot>
        <!-- TODO Show discount prices -->
        <!-- <tr>
          <td class="text-left" colspan="2"><b>{{ $t('orderDetails.tableColumn.discount') }}</b></td>
          <td class="text-right"><b>{{ orderDetails.formattedDiscountedPrice }}</b></td>
        </tr>-->
        <tr>
          <td class="text-left" colspan="2"><b>{{ $t('orderDetails.tableColumn.total') }}</b></td>
          <td class="text-right"><b>{{ orderDetails.formattedTotal }}</b></td>
        </tr>
      </template>
    </Table>


    <Table
      v-if="showPaymentsTable"
      small
      :items="orderDetails.paymentsList"
      :fields="paymentFields"
      :responsive="true"
      :striped="true"
      :hover="true"
      :tbody-tr-attr="rowIdentifierPaymentDetails"
      :aria-label="$t('orderDetails.label.tablePaymentDetails')"
    >
    </Table>
  </div>

</template>

<script>
import Table from "@/components/data-display/Table.vue";
import orderMixin from "@/components/mixins/orderMixin";
import VueLoading from 'vue-loading-overlay';
import QrcodeVue from "qrcode.vue";
import {Order, OrderTypes} from "@/constants/constants";
import {getOrderStatusLabel as resolveOrderStatusLabel, getOrderTypeLabel as resolveOrderTypeLabel} from "@/utils/orderUtils";
import {isNotEmpty} from "@/utils/displayUtils";
import {formatTimeLocale} from "@/utils/timeUtils";
import {formatDateLocale} from "@/utils/dateUtils";

export default {
  mixins: [orderMixin],
  components: {
    QrcodeVue,
    Table,
    VueLoading,
  },
  props: {
    guid: {
      type: String,
      required: true
    },
    shopId: {
      type: Number,
      required: false
    },
    showStatusLabel: {
      type: Boolean,
      default: true
    },
    showPaymentsTable: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      orderDetails: {},
      errorMessage: null,
      isLoading: false,
    };
  },
  computed:{
    OrderTypes() {
      return OrderTypes
    },
    config(){
      return this.$config;
    },
    fields() {
      return [
        { key: 'name', label: this.$t('orderDetails.tableColumn.article'), sortable: false },
        { key: 'quantity', label: this.$t('orderDetails.tableColumn.quantity'), sortable: false, class: 'text-right' },
        { key: 'totalAmount', label: this.$t('orderDetails.tableColumn.amount'), sortable: false, class: 'text-right',
          formatter: (value, key, item) => item.formattedDiscountedPrice ? item.formattedDiscountedPrice : item.formattedPrice ? item.formattedPrice : ''
        },
      ];
    },
    paymentFields() {
      return [
        {
          key: 'paymentType',
          label: this.$t('transactionDetails.tableColumn.paymentMethod'),
          class: 'text-left'
        },
        {
          key: 'formattedPaymentAmount',
          label: this.$t('transactionDetails.tableColumn.amount'), class: 'text-right' }
      ];
    },
    orderDetailRows() {
      const showTime = this.config?.ORDER?.shop?.timeSlots?.enabled;

      const rows = [];
      if (this.isNotEmpty(this.orderDetails.orderId)) {
        rows.push({ key: 'orderId', value: this.orderDetails.orderId });
      }
      if (this.showStatusLabel && this.isNotEmpty(this.orderDetails.status)) {
        rows.push({ key: 'status', value: this.getOrderStatusLabel(this.orderDetails) });
      }
      if (this.isNotEmpty(this.orderDetails.orderType)) {
        rows.push({ key: 'orderType', value: this.getOrderTypeLabel(this.orderDetails.orderType) });
      }
      // DINEIN and TAKEAWAY
      if (this.orderDetails.orderType === this.OrderTypes.DINE_IN || this.orderDetails.orderType === this.OrderTypes.TAKEAWAY) {
        if (this.isNotEmpty(this.orderDetails.date)) {
          rows.push({ key: 'pickupDate', value: formatDateLocale(this.orderDetails.date) });
        }

        if (showTime) {
          if (this.config.ORDER.shop.timeSlots.showOnlyToTime && this.isNotEmpty(this.orderDetails.timeTo)) {
            rows.push({ key: 'pickupTime', value: formatTimeLocale(this.orderDetails.timeTo) });
          } else if (this.isNotEmpty(this.orderDetails.timeFrom) && this.isNotEmpty(this.orderDetails.timeTo)) {
            rows.push({ key: 'pickupTime', value: `${formatTimeLocale(this.orderDetails.timeFrom)} - ${formatTimeLocale(this.orderDetails.timeTo)}` });
          }
        }

        if (this.isNotEmpty(this.orderDetails.shop?.name)) {
          rows.push({ key: 'location', value: this.orderDetails.shop?.name });
        }
      }
      // DELIVERY
      else if (this.orderDetails.orderType === this.OrderTypes.DELIVERY) {

        const showDateForDelivery = this.config?.ORDER?.shop?.dateOptions?.showForDelivery;
        if (showDateForDelivery) {
          if (this.isNotEmpty(this.orderDetails.date)) {
            rows.push({ key: 'deliveryDate', value: formatDateLocale(this.orderDetails.date) });
          }
        }

        const showTimeForDelivery = this.config?.ORDER?.shop?.timeSlots?.showForDelivery;
        if (showTime && showTimeForDelivery) {
          if (this.config.ORDER.shop.timeSlots.showOnlyToTime && this.isNotEmpty(this.orderDetails.timeTo)) {
            rows.push({ key: 'deliveryTime', value: formatTimeLocale(this.orderDetails.timeTo) });
          } else if (this.isNotEmpty(this.orderDetails.timeFrom) && this.isNotEmpty(this.orderDetails.timeTo)) {
            rows.push({ key: 'deliveryTime', value: `${formatTimeLocale(this.orderDetails.timeFrom)} - ${formatTimeLocale(this.orderDetails.timeTo)}` });
          }
        }
      }
      // Fallback for other types
      else {
        if (this.isNotEmpty(this.orderDetails.date)) {
          rows.push({ key: 'date', value: formatDateLocale(this.orderDetails.date) });
        }
        if (showTime) {
          if (this.config.ORDER.shop.timeSlots.showOnlyToTime && this.isNotEmpty(this.orderDetails.timeTo)) {
            rows.push({ key: 'time', value: formatTimeLocale(this.orderDetails.timeTo) });
          } else if (this.isNotEmpty(this.orderDetails.timeFrom) && this.isNotEmpty(this.orderDetails.timeTo)) {
            rows.push({ key: 'time', value: `${formatTimeLocale(this.orderDetails.timeFrom)} - ${formatTimeLocale(this.orderDetails.timeTo)}` });
          }
        }
      }
      if (this.isNotEmpty(this.orderDetails.table && this.orderDetails.table.id)) {
        rows.push({ key: 'info', value: this.$t('orderDetails.label.tableDelivery', [this.orderDetails.table.code]) });
      }
      if (this.isNotEmpty(this.orderDetails.custom8)) {
        rows.push({ key: 'pickupTime', value: this.orderDetails.custom8 });
      }
      if (this.isNotEmpty(this.orderDetails.pickupOrderNumber)) {
        rows.push({ key: 'pickupOrderNumber', value: this.orderDetails.pickupOrderNumber });
      }

      const isTakeawayByOrderTypeId = Number(this.orderDetails?.orderTypeId) === Order.orderTypId.TAKEAWAY;
      if (isTakeawayByOrderTypeId && this.isNotEmpty(this.orderDetails.iqsrOrderNum)) {
        rows.push({ key: 'iqsrOrderNum', value: this.orderDetails.iqsrOrderNum });
      }
      if (this.isNotEmpty(this.orderDetails.custom9)) {
        rows.push({ key: 'comment', value: this.orderDetails.custom9 });
      }
      return rows;
    },
  },
  methods: {
    isNotEmpty,
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
    },
    rowIdentifier(item, type) {
      if (type !== 'row') {
        return {};
      }
      return {
        'title': this.$t('orderDetails.tooltip.name') + ' ' + item.name
      }
    },
    rowIdentifierPaymentDetails(item, type) {
      if (type !== 'row') {
        return {};
      }
      return {
        'title': this.$t('orderDetails.tooltip.name') + ' ' + item.paymentType
      }
    },
    handleOrderDetailsError(error) {
      this.errorMessage = error || this.$t('orderDetails.messages.error.loadOrderDetails');
    },
    getOrderTypeLabel(orderType) {
      return resolveOrderTypeLabel(orderType, this.$t.bind(this), this.$te?.bind(this));
    },
    getOrderStatusLabel(order) {
      return resolveOrderStatusLabel(order?.statusId, order?.status, this.$t.bind(this), this.$te?.bind(this));
    },
  },
  async created() {
    this.isLoading = true;
    try {
      await this.getOrderDetails(this.guid, this.shopId);
    } catch (e) {
      this.handleOrderDetailsError(e);
    } finally {
      this.isLoading = false;
    }
  },
  mounted() {
    this.$root.$on('payment-methods-error', this.handleOrderDetailsError);
  },
  beforeDestroy() {
    this.$root.$off('payment-methods-error', this.handleOrderDetailsError);
  },
};
</script>

<style scoped lang="scss">
@import '@/css/customizer';

.order-details-qrcode-row {
  width: 50%;
  display: flex;
  justify-content: left;
  margin-bottom: 1.5rem;
}
.order-details-qrcode-full {
  display: flex;
  justify-content: center;
  align-items: center;
}
.order-details-qrcode-full .qrcode-img {
  width: 100%;
  max-width: 220px;
  height: auto;
  object-fit: contain;
  margin: 0 auto;
}

.order-details-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
}
.order-details-info {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.order-details-info p {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 0.5rem;
}
@media (max-width: 768px) {
  .order-details-qrcode-full .qrcode-img {
    max-width: 140px;
  }
}
</style>
